/* eslint-disable react/jsx-curly-newline */
import React, { useContext, useState, memo } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Typography,
  Button,
  Select,
  DatePicker,
  TreeSelect,
  Radio,
  Switch,
  message,
} from 'antd';
import XLSX from 'xlsx';
import { FormInstance } from 'antd/lib/form';
import { ControlOutlined } from '@ant-design/icons';
import { sendEventAnalytics } from '../../../analytics/analytics';
import API from '../../../api/API';
import FormDataContext from '../../../contexts/formData.context';
import eheLogo from '../../../images/loginLogo.svg';
import rspLogo from '../../../images/rsp-logo.svg';
import { IStaff } from '../../../models/staff';
import './Filters.css';
import { IState } from '../../../models/state';
import { ICounty } from '../../../models/county';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option, OptGroup } = Select;

interface IProps {
  form: FormInstance;
  onSearch: () => void;
  onReset: () => void;
  onSelectChange: (value: any) => void;
  createSearchParams: () => any;
}

const Filters: React.FC<IProps> = (props: IProps) => {
  // #region State, Props, Context, etc...

  const [showFilters, setShowFilters] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const {
    agencies,
    formats,
    principal,
    additional,
    region,
    states,
    jurisdictions,
  } = useContext(FormDataContext);
  const { form, onSearch, onReset, onSelectChange, createSearchParams } = props;

  // #endregion

  // #region Helper Functions

  const onFilterClick = () => {
    setShowFilters(!showFilters);
  };

  /*
   * When we click on the download button, this method creates the search params
   * from our current filter values and sends out a API request.
   *
   * If there is a successful response, we use SheetJS to download the file to
   * the local machine.
   */
  const onDownload = async () => {
    try {
      setDownloading(true);

      const params = createSearchParams();
      const resp = await API.get('/event/download', { params });
      const worksheet = XLSX.utils.json_to_sheet(resp.data);
      const newWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(newWorkbook, worksheet, 'SheetJS');
      XLSX.writeFile(newWorkbook, 'EHE Tracker - Events.xlsx');

      sendEventAnalytics('View All Events', 'Download');
      setDownloading(false);
    } catch {
      message.error('Looks like something went wrong.');
    }
  };

  /*
   * This method loops through our additional attendees array and filters
   * out all the orgs that are not our current agency. Then we generate
   * the select options to display.
   */
  const renderOneAdditionalAttendeesDropdownOptions = (
    array: IStaff[],
    currentAgency: string,
  ): JSX.Element => {
    return (
      <OptGroup
        key={`${currentAgency}|additionalDropdown`}
        label={currentAgency}
      >
        {array
          .filter((s: IStaff) => s.org === currentAgency)
          .map((st: IStaff) => (
            <Option
              key={`${st.name}|${st.org}|attendeesFilter`}
              value={st.name}
            >
              {st.name}
            </Option>
          ))}
      </OptGroup>
    );
  };

  /*
   * Here, we are getting the distinct agencies in our additional array
   * and calling our helper method to generate the dropdown options.
   */
  const renderAllAdditionalAttendeesDropdownOptions = (
    array: IStaff[],
  ): JSX.Element[] => {
    const distinctAgencies = Array.from(
      new Set<string>(array.map((e) => e.org)),
    );
    const renderedList = [];

    for (let i = 0; i < distinctAgencies.length; i += 1) {
      const currentAgency = distinctAgencies[i];
      renderedList.push(
        renderOneAdditionalAttendeesDropdownOptions(array, currentAgency),
      );
    }

    return renderedList;
  };

  const generateJurisdictionCountiesDropdownOptions = (
    array: ICounty[],
    currentState: string,
  ) => {
    const dropdownOptions: any[] = [];
    let currentCounty;

    array.forEach((county: ICounty) => {
      const currentValue = `${county.name} (${currentState})`;

      currentCounty = {
        title: county.name,
        value: currentValue,
      };

      dropdownOptions.push(currentCounty);
    });

    return dropdownOptions;
  };

  const generateJurisdictionStateDropdownOptions = (array: IState[]) => {
    const dropdownOptions: any[] = [];
    let currentState;

    array.forEach((state: IState) => {
      currentState = {
        title: state.name,
        value: state.name,
        children: generateJurisdictionCountiesDropdownOptions(
          state.counties,
          state.abbreviation,
        ),
      };

      dropdownOptions.push(currentState);
    });

    return dropdownOptions;
  };

  // #endregion

  // #region Render Section

  return (
    <Form form={form} size="large">
      <Row className="Filters-control-row">
        {/*
         * Container for Search box and Filter button
         */}
        <Col span={12}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="q">
                <Search placeholder="Search Events..." onSearch={onSearch} />
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<ControlOutlined />}
                onClick={onFilterClick}
                className="Filters-filter-button"
              >
                {showFilters ? 'Close' : 'Filter'}
                {showFilters && (
                  <>
                    <div className="Filters-filter-pointer-border" />
                    <div className="Filters-filter-pointer" />
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Col>
        {/*
            Container for Select and Download button
          */}
        <Col span={12}>
          <Row gutter={16} justify="end" align="middle">
            <Col>
              <Text className="Filters-select-label">Per Page:</Text>
            </Col>
            <Col>
              <Form.Item noStyle name="limit" initialValue="25">
                <Select onChange={onSelectChange}>
                  <Option value={25}>25</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Button type="primary" onClick={onDownload} loading={downloading}>
                Download (XLSX)
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      {/*
        Container for filter options
      */}
      <Row
        className="Filters-search-filters"
        style={showFilters ? {} : { display: 'none' }}
      >
        {/*
          Switch Section
        */}
        <Row gutter={32} className="Filters-switch-row">
          <Col>
            <Form.Item
              name="myEvents"
              valuePropName="checked"
              noStyle
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            <Text className="Filters-switch-label">Only My Events</Text>
          </Col>
          <Col>
            <Form.Item
              name="flagged"
              valuePropName="checked"
              noStyle
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            <Text className="Filters-switch-label">Flagged Events</Text>
          </Col>
          <Col>
            <Form.Item
              name="notReviewed"
              valuePropName="checked"
              noStyle
              initialValue={false}
            >
              <Switch />
            </Form.Item>
            <Text className="Filters-switch-label">
              Events That Need Review
            </Text>
          </Col>
        </Row>
        <Row gutter={32} className="Filters-form-row">
          <Col span={4}>
            {/*
             Date Range section
            */}
            <Title level={4}>Date Range:</Title>
            <Text strong>Start</Text>
            <Form.Item name="start">
              <DatePicker
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                className="Filters-datepicker"
              />
            </Form.Item>
            <Text strong>End</Text>
            <Form.Item name="end">
              <DatePicker
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                className="Filters-datepicker"
              />
            </Form.Item>
            {/*
             Location (State) section
            */}
            <Title level={4}>Location (State):</Title>
            <Form.Item name="state">
              <Select
                placeholder="State"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {states.map((st) => (
                  <Option key={st.id} value={st.abbreviation}>
                    {st.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={10}>
            {/*
            Participating Agencies section
          */}
            <Title level={4}>Participating Agencies:</Title>
            <Form.Item name="agencies">
              <Select
                mode="multiple"
                allowClear
                maxTagCount={3}
                maxTagTextLength={10}
                showArrow
                showSearch
                placeholder="All"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {agencies.map((a) => (
                  <Option key={a.id} value={a.abbreviation}>
                    {a.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {/*
              Format section
            */}
            <Title level={4}>Format:</Title>
            <Form.Item name="format">
              <Select
                placeholder="All"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {formats.map((f) => (
                  <Option key={f.id} value={f.type}>
                    {f.type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {/*
              Attendees & Speakers section
            */}
            <Title level={4}>Attendees & Speakers:</Title>
            <Form.Item name="attendees">
              <Select
                mode="multiple"
                allowClear
                maxTagCount={3}
                maxTagTextLength={10}
                showArrow
                placeholder="All"
              >
                <OptGroup label="HHS Principals">
                  {principal.map((st: IStaff) => (
                    <Option
                      key={`${st.name}|${st.org}|attendeesFilter`}
                      value={st.name}
                    >
                      {st.name}
                    </Option>
                  ))}
                </OptGroup>
                {renderAllAdditionalAttendeesDropdownOptions(additional)}
                <OptGroup label="OASH/PACE Officers (HHS Regions)">
                  {region.map((st: IStaff) => (
                    <Option
                      key={`${st.name}|${st.org}|attendeesFilter`}
                      value={st.name}
                    >
                      {st.name}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            </Form.Item>
          </Col>
          {/*
            Programs Section
          */}
          <Col span={10}>
            <Title level={4}>Programs:</Title>
            <Row className="Filters-card">
              <Row className="Filters-card-row">
                <Col span={6} className="Filters-card-logo-container">
                  <img
                    src={eheLogo}
                    className="Filters-card-logo"
                    alt="ehe logo"
                  />
                </Col>
                <Col span={18}>
                  <Text strong>EHE Jurisdiction Visit:</Text>
                  <Form.Item name="ehe" className="Filters-card-form-item">
                    <Radio.Group>
                      <Radio value={1}>Yes</Radio>
                      <Radio value={0}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item name="jurisdictions" className="Filters-">
                    <TreeSelect
                      placeholder="All"
                      treeData={generateJurisdictionStateDropdownOptions(
                        jurisdictions,
                      )}
                      treeCheckable
                      maxTagCount={3}
                      maxTagTextLength={5}
                      allowClear
                      showArrow
                      showSearch
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="Filters-card-row">
                <Col span={6} className="Filters-card-logo-container">
                  <img
                    src={rspLogo}
                    className="Filters-card-logo"
                    alt="ehe logo"
                  />
                </Col>
                <Col span={18}>
                  <Text strong>RSP:</Text>
                  <Form.Item name="rsp" className="Filters-card-form-item">
                    <Radio.Group>
                      <Radio value={1} className="Filters-radio">
                        Yes
                      </Radio>
                      <Radio value={0} className="Filters-radio">
                        No
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Row>
          </Col>
        </Row>
        {/*
          Buttons
        */}
        <Row gutter={16} justify="end" className="Filters-button-row">
          <Col>
            <Button onClick={onReset}>Reset</Button>
          </Col>
          <Col>
            <Button onClick={onSearch} type="primary">
              Apply
            </Button>
          </Col>
        </Row>
      </Row>
    </Form>
  );

  // #endregion
};

export default memo(Filters);
